// src/components/landing/FlashcardsBlock.tsx
// Bloco 01 — Flashcards | GRAIA Landing
// Prefixo CSS: flkb-

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shuffle, Zap, Layers } from 'lucide-react';
import './FlashcardsBlock.css';

// ─────────────────────────────────────────────
// CARDS FIXOS — 8 flashcards de medicina
// ─────────────────────────────────────────────
const DEMO_CARDS = [
    {
        id: 1,
        front: 'Qual estrutura do coração atua como marcapasso fisiológico?',
        back: 'Nó sinoatrial (SA), localizado no átrio direito, responsável por iniciar o ritmo cardíaco.',
        tag: 'Sistema Cardiovascular',
    },
    {
        id: 2,
        front: 'Qual neurotransmissor está reduzido na doença de Parkinson?',
        back: 'Dopamina, devido à degeneração dos neurônios da substância negra pars compacta.',
        tag: 'Sistema Nervoso',
    },
    {
        id: 3,
        front: 'Qual é o anticorpo mais abundante no plasma humano?',
        back: 'IgG — responsável pela maior parte da imunidade humoral e capaz de atravessar a placenta.',
        tag: 'Imunologia',
    },
    {
        id: 4,
        front: 'Qual é a função principal da valva mitral?',
        back: 'Impedir o refluxo de sangue do ventrículo esquerdo para o átrio esquerdo durante a sístole.',
        tag: 'Sistema Cardiovascular',
    },
    {
        id: 5,
        front: 'Qual nervo está frequentemente lesado na fratura do colo cirúrgico do úmero?',
        back: 'O nervo axilar, causando paralisia do músculo deltoide e perda de sensibilidade na face lateral do ombro.',
        tag: 'Ortopedia',
    },
    {
        id: 6,
        front: 'Qual é a principal forma de transporte de CO₂ no sangue?',
        back: 'Bicarbonato (HCO₃⁻), formado nos eritrócitos pela ação da anidrase carbônica.',
        tag: 'Fisiologia respiratória',
    },
    {
        id: 7,
        front: 'Quais são as células de Kupffer?',
        back: 'Macrófagos residentes do fígado, localizados nos sinusoides hepáticos, responsáveis pela fagocitose de patógenos e dedritos celulares.',
        tag: 'Histologia',
    },
    {
        id: 8,
        front: 'Qual é a principal causa de insuficiência renal aguda intrínseca?',
        back: 'Necrose tubular aguda (NTA), geralmente causada por isquemia ou nefrotoxinas.',
        tag: 'Nefrologia',
    },
];

const RATINGS = [
    { value: 1, label: 'Errei', color: '#dc2626', polarity: 'negative' },
    { value: 2, label: 'Difícil', color: '#ea580c', polarity: 'negative' },
    { value: 3, label: 'Ok', color: '#ca8a04', polarity: 'neutral' },
    { value: 4, label: 'Fácil', color: '#16a34a', polarity: 'positive' },
    { value: 5, label: 'Perfeito', color: '#184C18', polarity: 'positive' },
];

const MASTERY_DELTA: Record<number, number> = {
    1: -6, 2: -4, 3: 3, 4: 7, 5: 10,
};

const PILLARS = [
    {
        icon: Shuffle,
        id: 'queue',
        title: 'Fila Inteligente',
        text: 'Cada card recebe prioridade dinâmica baseada no seu histórico de desempenho.',
    },
    {
        icon: Zap,
        id: 'realtime',
        title: 'Ajuste em Tempo Real',
        text: 'Errou? A prioridade aumenta. Acertou? O intervalo até a próxima revisão se estende.',
    },
    {
        icon: Layers,
        id: 'flow',
        title: 'Fluxo Contínuo',
        text: 'Pré-carregamento invisível. Sem travamentos. Sem interrupções.',
    },
];

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const FlashcardsBlock: React.FC = () => {
    const blockRef = useRef<HTMLElement>(null);
    const mockColRef = useRef<HTMLDivElement>(null);

    const [cardIndex, setCardIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [mastery, setMastery] = useState(42);
    const [masteryPulse, setMasteryPulse] = useState<'up' | 'down' | null>(null);
    const [activePillar, setActivePillar] = useState<string | null>(null);
    const [blockVisible, setBlockVisible] = useState(false);
    const [pressedRating, setPressedRating] = useState<number | null>(null);
    const [isFloating, setIsFloating] = useState(false);

    const currentCard = DEMO_CARDS[cardIndex];

    // Intersection Observer — ativa toda a coreografia CSS
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setBlockVisible(true);
                    observer.disconnect(); // dispara uma vez só
                }
            },
            { threshold: 0.12 }
        );
        if (blockRef.current) observer.observe(blockRef.current);
        return () => observer.disconnect();
    }, []);

    // Scroll — floating shadow no card sticky
    useEffect(() => {
        const handleScroll = () => {
            if (!blockRef.current) return;
            setIsFloating(blockRef.current.getBoundingClientRect().top < 0);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleFlip = useCallback(() => {
        if (isTransitioning) return;
        setIsFlipped(true);
    }, [isTransitioning]);

    const handleAnswer = useCallback((rating: number) => {
        if (isTransitioning) return;

        setPressedRating(rating);
        setMastery(prev => Math.min(100, Math.max(0, prev + (MASTERY_DELTA[rating] ?? 0))));
        setActivePillar(rating <= 2 ? 'realtime' : 'flow');

        setTimeout(() => {
            setIsTransitioning(true);
            setIsFlipped(false);
            setTimeout(() => {
                setCardIndex(prev => (prev + 1) % DEMO_CARDS.length);
                setIsTransitioning(false);
                setPressedRating(null);
                setActivePillar(null);
            }, 180);
        }, 120);
    }, [isTransitioning]);

    const prevMastery = useRef(mastery);
    useEffect(() => {
        if (prevMastery.current !== mastery) {
            const direction = mastery > prevMastery.current ? 'up' : 'down';
            setMasteryPulse(direction);
            const t = setTimeout(() => setMasteryPulse(null), 500);
            prevMastery.current = mastery;
            return () => clearTimeout(t);
        }
    }, [mastery]);

    return (
        <section
            className={`flkb-section ${blockVisible ? 'flkb-section--visible' : ''}`}
            ref={blockRef}
        >
            <div className="flkb-container">

                {/* ── CABEÇALHO ── */}
                <div className="flkb-header">
                    <p className="flkb-label">Flashcards Inteligentes</p>
                    <h2 className="flkb-headline">
                        Estude no ritmo <br />do seu cérebro.
                    </h2>
                    <p className="flkb-subheadline">
                        O sistema reorganiza os flashcards conforme seu desempenho.
                        Você vê o conteúdo certo, no momento certo.
                    </p>
                </div>

                {/* ── GRID PRINCIPAL ── */}
                <div className="flkb-grid">

                    {/* ─── COLUNA ESQUERDA: MOCK ─── */}
                    <div
                        className={`flkb-mock-col ${isFloating ? 'flkb-mock-col--floating' : ''}`}
                        ref={mockColRef}
                    >
                        <div className="flkb-mock-shell">

                            <div className="flkb-mock-header">
                                <div className="flkb-mock-header-left">
                                    <span className="flkb-mock-logo">GRAIA</span>
                                    <span className="flkb-mock-sep" />
                                    <span className="flkb-mock-mode">Revisão Adaptativa</span>
                                </div>
                                <span className="flkb-mock-count">
                                    {cardIndex + 1} / {DEMO_CARDS.length}
                                </span>
                            </div>

                            <div className="flkb-mastery-wrap">
                                <div className="flkb-mastery-row">
                                    <span className="flkb-mastery-label">Domínio</span>
                                    <span
                                        className={`flkb-mastery-value ${masteryPulse === 'up' ? 'flkb-mastery-value--up' :
                                            masteryPulse === 'down' ? 'flkb-mastery-value--down' : ''
                                            }`}
                                    >
                                        {mastery}%
                                    </span>
                                </div>
                                <div className="flkb-mastery-track">
                                    <div
                                        className={`flkb-mastery-fill ${masteryPulse === 'up' ? 'flkb-mastery-fill--pulse-up' :
                                            masteryPulse === 'down' ? 'flkb-mastery-fill--pulse-down' : ''
                                            }`}
                                        style={{ width: `${mastery}%` }}
                                    />
                                </div>
                            </div>

                            <div className={`flkb-card-tag-row ${isTransitioning ? 'flkb-content--transitioning' : ''}`}>
                                <span className="flkb-card-tag">{currentCard.tag}</span>
                            </div>

                            <div className={`flkb-card ${isTransitioning ? 'flkb-content--transitioning' : ''}`}>
                                {!isFlipped ? (
                                    <div className="flkb-card-face flkb-card-front">
                                        <p className="flkb-card-face-hint">Pergunta</p>
                                        <p className="flkb-card-question">{currentCard.front}</p>
                                    </div>
                                ) : (
                                    <div className="flkb-card-face flkb-card-back">
                                        <p className="flkb-card-face-hint">Resposta</p>
                                        <p className="flkb-card-answer">{currentCard.back}</p>
                                    </div>
                                )}
                            </div>

                            <div className={`flkb-actions ${isTransitioning ? 'flkb-content--transitioning' : ''}`}>
                                {!isFlipped ? (
                                    <button
                                        className="flkb-btn-reveal"
                                        onClick={handleFlip}
                                        disabled={isTransitioning}
                                    >
                                        Ver resposta
                                    </button>
                                ) : (
                                    <div className="flkb-ratings">
                                        <p className="flkb-ratings-label">Como foi?</p>
                                        <div className="flkb-ratings-row">
                                            {RATINGS.map(r => (
                                                <button
                                                    key={r.value}
                                                    className={[
                                                        'flkb-rating-btn',
                                                        `flkb-rating-btn--${r.polarity}`,
                                                        pressedRating === r.value ? 'flkb-rating-btn--pressed' : '',
                                                    ].filter(Boolean).join(' ')}
                                                    style={{ '--flkb-rating-color': r.color } as React.CSSProperties}
                                                    onClick={() => handleAnswer(r.value)}
                                                    disabled={isTransitioning}
                                                >
                                                    <span className="flkb-rating-num">{r.value}</span>
                                                    <span className="flkb-rating-text">{r.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>

                    {/* ─── COLUNA DIREITA: PILARES ─── */}
                    <div className="flkb-pillars-col">
                        {PILLARS.map((pillar) => {
                            const Icon = pillar.icon;
                            const isActive = activePillar === pillar.id;
                            return (
                                <div
                                    key={pillar.id}
                                    className={`flkb-pillar ${isActive ? 'flkb-pillar--active' : ''}`}
                                >
                                    <div className="flkb-pillar-accent" />
                                    <div className="flkb-pillar-icon-wrap">
                                        <Icon size={18} strokeWidth={1.75} />
                                    </div>
                                    <div className="flkb-pillar-body">
                                        <h3 className="flkb-pillar-title">{pillar.title}</h3>
                                        <p className="flkb-pillar-text">{pillar.text}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── FRASE DE ANCORAGEM ── */}
                <div className="flkb-anchor">
                    <p className="flkb-anchor-text">
                        Não é repetição.<br />
                        <strong>É reorganização inteligente do seu conhecimento.</strong>
                    </p>
                </div>

            </div>
        </section>
    );
};

export default FlashcardsBlock;